/**
 * User Usage Details Page
 * 
 * Comprehensive view of a specific user's usage analytics,
 * subscription details, and activity history for admin monitoring.
 */

import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { 
  ArrowLeft, 
  User, 
  Building2, 
  CreditCard, 
  MessageSquare, 
  Users, 
  Eye, 
  HardDrive,
  Bot,
  TrendingUp,
  Activity,
  Calendar,
  AlertCircle,
  CheckCircle
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { adminApi } from '../../services/admin/adminApi'
import { logger } from '../../utils/logger'
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts'

interface UserUsageData {
  user: {
    id: string
    email: string
    full_name: string | null
    created_at: string | null
    is_active: boolean
    is_verified: boolean
  }
  organization: {
    id: string
    name: string
    status: string
    created_at: string | null
  } | null
  subscription: {
    plan_name: string
    plan_type: string | null
    status: string
    price: string
  }
  current_usage: {
    messages_used: number
    unique_visitors: number
    team_members: number
    storage_mb: number
    period_start: string | null
    period_end: string | null
  }
  usage_percentages: {
    messages: number
    visitors: number
    team_members: number
    storage: number
  }
  usage_trends: {
    messages_by_day: Record<string, number>
    total_messages_period: number
  }
  chatbots: Array<{
    id: string
    name: string
    is_active: boolean
    created_at: string | null
  }>
  recent_conversations: Array<{
    id: string
    chatbot_id: string
    status: string
    created_at: string | null
    message_count: number
  }>
  statistics: {
    total_chatbots: number
    active_chatbots: number
    total_conversations: number
    conversations_this_period: number
  }
}

export const UserUsageDetails: React.FC = () => {
  const { userId } = useParams<{ userId: string }>()
  const [userData, setUserData] = useState<UserUsageData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [period, setPeriod] = useState('30d')

  useEffect(() => {
    fetchUserUsageData()
  }, [userId, period])

  const fetchUserUsageData = async () => {
    if (!userId) return
    
    setLoading(true)
    setError(null)
    
    try {
      const data = await adminApi.getUserUsageDetails(userId, period)
      setUserData(data)
      logger.debug('User usage data loaded', { userId, data })
    } catch (err: any) {
      setError(err.message || 'Failed to load user usage data')
      logger.error('Failed to load user usage data', { error: err })
    } finally {
      setLoading(false)
    }
  }

  // Convert messages_by_day to chart data
  const chartData = userData?.usage_trends.messages_by_day 
    ? Object.entries(userData.usage_trends.messages_by_day).map(([date, count]) => ({
        date: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        messages: count
      }))
    : []

  const getUsageColor = (percentage: number) => {
    if (percentage >= 90) return 'text-red-600'
    if (percentage >= 75) return 'text-orange-600'
    if (percentage >= 50) return 'text-yellow-600'
    return 'text-green-600'
  }

  const getUsageBarColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-500'
    if (percentage >= 75) return 'bg-orange-500'
    if (percentage >= 50) return 'bg-yellow-500'
    return 'bg-green-500'
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading user usage data...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-600" />
              <p className="text-red-700">{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (!userData) {
    return null
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/admin/users">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Users
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">User Usage Details</h1>
            <p className="text-gray-600">{userData.user.email}</p>
          </div>
        </div>
        
        {/* Period Selector */}
        <div className="flex space-x-2">
          {['7d', '30d', '90d'].map((p) => (
            <Button
              key={p}
              variant={period === p ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setPeriod(p)}
            >
              {p === '7d' ? 'Last 7 Days' : p === '30d' ? 'Last 30 Days' : 'Last 90 Days'}
            </Button>
          ))}
        </div>
      </div>

      {/* User & Organization Info */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* User Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center text-base">
              <User className="h-4 w-4 mr-2" />
              User Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>
              <p className="text-sm text-gray-600">Name</p>
              <p className="font-medium">{userData.user.full_name || 'Not provided'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Email</p>
              <p className="font-medium">{userData.user.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <div className="flex items-center space-x-2">
                {userData.user.is_active ? (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    <CheckCircle className="h-3 w-3 mr-1" />
                    Active
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Inactive
                  </span>
                )}
                {userData.user.is_verified && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    Verified
                  </span>
                )}
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-600">Member Since</p>
              <p className="font-medium">
                {userData.user.created_at 
                  ? new Date(userData.user.created_at).toLocaleDateString()
                  : 'Unknown'}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Organization Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center text-base">
              <Building2 className="h-4 w-4 mr-2" />
              Organization
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {userData.organization ? (
              <>
                <div>
                  <p className="text-sm text-gray-600">Name</p>
                  <p className="font-medium">{userData.organization.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    userData.organization.status === 'active' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {userData.organization.status}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Created</p>
                  <p className="font-medium">
                    {userData.organization.created_at 
                      ? new Date(userData.organization.created_at).toLocaleDateString()
                      : 'Unknown'}
                  </p>
                </div>
              </>
            ) : (
              <p className="text-gray-500 italic">No organization associated</p>
            )}
          </CardContent>
        </Card>

        {/* Subscription Info */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center text-base">
              <CreditCard className="h-4 w-4 mr-2" />
              Subscription
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div>
              <p className="text-sm text-gray-600">Plan</p>
              <p className="font-medium">{userData.subscription.plan_name}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Type</p>
              <p className="font-medium capitalize">{userData.subscription.plan_type || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                userData.subscription.status === 'active' 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {userData.subscription.status}
              </span>
            </div>
            <div>
              <p className="text-sm text-gray-600">Price</p>
              <p className="font-medium">{userData.subscription.price}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Current Usage Metrics */}
      <div>
        <h2 className="text-lg font-semibold mb-4">Current Usage</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Messages */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <MessageSquare className="h-4 w-4 text-blue-600" />
                    <p className="text-sm font-medium text-gray-600">AI Messages</p>
                  </div>
                  <p className="text-2xl font-bold mt-2">{userData.current_usage.messages_used.toLocaleString()}</p>
                  {userData.usage_percentages.messages !== undefined && (
                    <>
                      <p className={`text-sm mt-1 ${getUsageColor(userData.usage_percentages.messages)}`}>
                        {userData.usage_percentages.messages}% of limit
                      </p>
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${getUsageBarColor(userData.usage_percentages.messages)}`}
                          style={{ width: `${Math.min(userData.usage_percentages.messages, 100)}%` }}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Visitors */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <Eye className="h-4 w-4 text-green-600" />
                    <p className="text-sm font-medium text-gray-600">Unique Visitors</p>
                  </div>
                  <p className="text-2xl font-bold mt-2">{userData.current_usage.unique_visitors.toLocaleString()}</p>
                  {userData.usage_percentages.visitors !== undefined && (
                    <>
                      <p className={`text-sm mt-1 ${getUsageColor(userData.usage_percentages.visitors)}`}>
                        {userData.usage_percentages.visitors}% of limit
                      </p>
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${getUsageBarColor(userData.usage_percentages.visitors)}`}
                          style={{ width: `${Math.min(userData.usage_percentages.visitors, 100)}%` }}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Team Members */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <Users className="h-4 w-4 text-indigo-600" />
                    <p className="text-sm font-medium text-gray-600">Team Members</p>
                  </div>
                  <p className="text-2xl font-bold mt-2">{userData.current_usage.team_members}</p>
                  {userData.usage_percentages.team_members !== undefined && (
                    <>
                      <p className={`text-sm mt-1 ${getUsageColor(userData.usage_percentages.team_members)}`}>
                        {userData.usage_percentages.team_members}% of limit
                      </p>
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${getUsageBarColor(userData.usage_percentages.team_members)}`}
                          style={{ width: `${Math.min(userData.usage_percentages.team_members, 100)}%` }}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Storage */}
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <HardDrive className="h-4 w-4 text-purple-600" />
                    <p className="text-sm font-medium text-gray-600">Storage</p>
                  </div>
                  <p className="text-2xl font-bold mt-2">
                    {userData.current_usage.storage_mb >= 1024 
                      ? `${(userData.current_usage.storage_mb / 1024).toFixed(1)} GB`
                      : `${userData.current_usage.storage_mb} MB`}
                  </p>
                  {userData.usage_percentages.storage !== undefined && (
                    <>
                      <p className={`text-sm mt-1 ${getUsageColor(userData.usage_percentages.storage)}`}>
                        {userData.usage_percentages.storage}% of limit
                      </p>
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className={`h-2 rounded-full ${getUsageBarColor(userData.usage_percentages.storage)}`}
                          style={{ width: `${Math.min(userData.usage_percentages.storage, 100)}%` }}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Usage Trends Chart */}
      {chartData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <TrendingUp className="h-4 w-4 mr-2" />
              Message Usage Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line 
                    type="monotone" 
                    dataKey="messages" 
                    stroke="#3B82F6" 
                    strokeWidth={2}
                    dot={{ fill: '#3B82F6' }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="text-sm text-gray-600 mt-2 text-center">
              Total messages in period: {userData.usage_trends.total_messages_period.toLocaleString()}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Statistics and Activity */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Chatbots */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <Bot className="h-4 w-4 mr-2" />
                Chatbots
              </div>
              <span className="text-sm font-normal text-gray-600">
                {userData.statistics.active_chatbots} / {userData.statistics.total_chatbots} active
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {userData.chatbots.length > 0 ? (
              <div className="space-y-2">
                {userData.chatbots.slice(0, 5).map((bot) => (
                  <div key={bot.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                    <div>
                      <p className="font-medium">{bot.name}</p>
                      <p className="text-sm text-gray-600">
                        Created {bot.created_at ? new Date(bot.created_at).toLocaleDateString() : 'Unknown'}
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      bot.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {bot.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                ))}
                {userData.chatbots.length > 5 && (
                  <p className="text-sm text-gray-600 text-center pt-2">
                    +{userData.chatbots.length - 5} more chatbots
                  </p>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No chatbots created</p>
            )}
          </CardContent>
        </Card>

        {/* Recent Conversations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center">
                <Activity className="h-4 w-4 mr-2" />
                Recent Conversations
              </div>
              <span className="text-sm font-normal text-gray-600">
                {userData.statistics.conversations_this_period} this period
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {userData.recent_conversations.length > 0 ? (
              <div className="space-y-2">
                {userData.recent_conversations.slice(0, 5).map((conv) => (
                  <div key={conv.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                    <div>
                      <p className="font-medium text-sm">Conversation #{conv.id.slice(-8)}</p>
                      <p className="text-sm text-gray-600">
                        {conv.message_count} messages • {new Date(conv.created_at!).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      conv.status === 'active' ? 'bg-green-100 text-green-800' : 
                      conv.status === 'closed' ? 'bg-gray-100 text-gray-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {conv.status}
                    </span>
                  </div>
                ))}
                {userData.recent_conversations.length > 5 && (
                  <p className="text-sm text-gray-600 text-center pt-2">
                    View all {userData.statistics.total_conversations} conversations
                  </p>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-4">No conversations in this period</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Summary Stats */}
      <Card className="bg-gray-50">
        <CardContent className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-gray-900">{userData.statistics.total_chatbots}</p>
              <p className="text-sm text-gray-600">Total Chatbots</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{userData.statistics.active_chatbots}</p>
              <p className="text-sm text-gray-600">Active Chatbots</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{userData.statistics.total_conversations}</p>
              <p className="text-sm text-gray-600">Total Conversations</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{userData.usage_trends.total_messages_period}</p>
              <p className="text-sm text-gray-600">Messages This Period</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}